import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useSite } from "../context/SiteContext";
import useReveal from "../hooks/useReveal";

export default function ClanMap() {
  useReveal();
  const { c, accent, mode } = useSite();
  const map = c.clanMap;
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  // init once
  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    const m = L.map(elRef.current, {
      scrollWheelZoom: false,
      attributionControl: true,
    }).setView([51.7, 10.2], 11);
    mapRef.current = m;
    setTimeout(() => m.invalidateSize(), 200);
    return () => {
      m.remove();
      mapRef.current = null;
    };
  }, []);

  // (re)draw tiles + markers when language / accent / theme changes
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    if (layerRef.current) {
      layerRef.current.forEach((l) => m.removeLayer(l));
    }
    const layers = [];

    const tileUrl = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
    const tiles = L.tileLayer(tileUrl, {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
      className: mode === "dark" ? "hh-map-dark" : "",
    }).addTo(m);
    layers.push(tiles);

    const bounds = [];
    const markerById = {};
    map.locations.forEach((loc) => {
      const html = `<div style="position:relative">
        <div style="width:20px;height:20px;background:${accent};border:2px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,.5)"></div>
      </div>`;
      const icon = L.divIcon({ html, className: "hh-pin", iconSize: [20, 20], iconAnchor: [10, 20] });
      const marker = L.marker([loc.lat, loc.lng], { icon }).addTo(m);
      marker.bindPopup(
        `<div style="font-family:'Cormorant Garamond',serif;min-width:180px">
          <div style="font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:${accent}">${loc.role}</div>
          <div style="font-size:20px;font-weight:600;margin:2px 0 4px">${loc.name}</div>
          <div style="font-size:14px;color:#555;line-height:1.35">${loc.note}</div>
        </div>`
      );
      layers.push(marker);
      markerById[loc.id] = marker;
      bounds.push([loc.lat, loc.lng]);
    });

    if (bounds.length) m.fitBounds(bounds, { padding: [60, 60], maxZoom: 12 });
    layerRef.current = layers;

    // focus a specific pin when arriving via "?loc=<id>" (from a subpage button)
    const focusId = new URLSearchParams(window.location.search).get("loc");
    if (focusId && markerById[focusId]) {
      const loc = map.locations.find((l) => l.id === focusId);
      setTimeout(() => {
        try {
          m.invalidateSize();
          m.setView([loc.lat, loc.lng], 13, { animate: false });
          markerById[focusId].openPopup();
        } catch (e) {
          /* map not ready */
        }
      }, 700);
    }
  }, [map, accent, mode]);

  return (
    <section id="map" className="bg-ink py-28 sm:py-40">
      <div className="max-w-[1220px] mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <span className="eyebrow">{map.label}</span>
          <h2 className="font-display text-4xl sm:text-6xl font-light leading-[1.02] text-parchment mt-5">
            {map.title}
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-parchment/60 font-light">{map.intro}</p>
        </div>

        <div className="reveal mt-14 grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 border border-[var(--c-border)]">
            <div ref={elRef} style={{ height: 460, width: "100%", background: "var(--c-surface)" }} />
          </div>
          <div className="flex flex-col gap-4">
            {map.locations.map((loc) => (
              <button
                key={loc.id}
                data-testid={`map-location-card-${loc.id}`}
                onClick={() => {
                  mapRef.current?.setView([loc.lat, loc.lng], 13, { animate: true });
                }}
                className="text-left border border-[var(--c-border)] bg-ink2 p-6 hover:border-gold/50 transition-colors group"
              >
                <span className="font-label text-[0.6rem] uppercase tracking-[0.2em] text-gold">{loc.role}</span>
                <h3 className="font-display text-2xl text-parchment mt-1 group-hover:text-gold transition-colors">{loc.name}</h3>
                <p className="mt-2 text-[0.95rem] leading-relaxed text-parchment/60 font-light">{loc.note}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
